export interface TripCoverageAssignment {
  status?: string;
  device_id?: string;
  device?: { id?: string; name?: string; model?: string } | null;
}

export interface TripCoverageTrip {
  id: string;
  assignments?: TripCoverageAssignment[] | null;
}

export type TripCoverageState = "live" | "closed_only" | "none";

export interface TripCoverageUnit {
  id: string;
  label: string;
}

export interface TripCoverage {
  state: TripCoverageState;
  liveUnits: TripCoverageUnit[];
  lastClosedDeviceId: string | null;
}

const LIVE_STATUSES = new Set(["active", "pending"]);

const toUnit = (assignment: TripCoverageAssignment): TripCoverageUnit | null => {
  const id = assignment.device_id || assignment.device?.id;
  if (!id) return null;
  return { id, label: assignment.device?.name || assignment.device?.model || id };
};

const uniqueUnits = (units: TripCoverageUnit[]) =>
  units.filter((unit, index) => units.findIndex((other) => other.id === unit.id) === index);

export const getTripCoverage = (trip: TripCoverageTrip | null | undefined): TripCoverage => {
  const assignments = Array.isArray(trip?.assignments) ? trip.assignments : [];
  const live = assignments.filter((a) => LIVE_STATUSES.has(a.status ?? ""));
  const closed = assignments.filter((a) => !LIVE_STATUSES.has(a.status ?? ""));
  const lastClosed = closed.map(toUnit).filter((u): u is TripCoverageUnit => u !== null).pop();

  const state: TripCoverageState =
    live.length > 0 ? "live" : assignments.length > 0 ? "closed_only" : "none";

  return {
    state,
    liveUnits: uniqueUnits(live.map(toUnit).filter((u): u is TripCoverageUnit => u !== null)),
    lastClosedDeviceId: lastClosed?.id ?? null,
  };
};

export const hasLiveAssignment = (trip: TripCoverageTrip | null | undefined) =>
  getTripCoverage(trip).state === "live";
